import { ReactElement, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useProjectTimezone } from 'store/project';

import { UserOnView } from 'shared/domain/user/types/view';
import { UserColumnsFactory } from './columns';
import { useUserDataGridSlotProps } from './useUserDataGridSlotProps';
import { EmptyListReason } from 'components/common/noTableContentInfo/NoTableContentInfo';
import { sxStyles } from './styles';
import { useAppNavigation } from 'contexts/navigation';
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  INITIAL_SORT_MODEL,
} from './constants';
import { DataGridInstanceName } from 'services/storage/keys';
import { SearchPhraseMismatchNoRowsOverlay } from 'components/common/dataGrid/noRowsOverlay';
import { DataGrid } from 'components/common/dataGrid';
import { createClientDataGridConfig } from 'components/common/dataGrid/config/DataGridConfig';

type UserDataGridProps = {
  data: UserOnView[];
  loading?: boolean;
  emptyListReason?: EmptyListReason;
};

export function UserDataGrid({
  data,
  loading = false,
  emptyListReason,
}: UserDataGridProps): ReactElement {
  const intl = useIntl();
  const timezone = useProjectTimezone();
  const { goToUserDetails } = useAppNavigation();

  const columns = useMemo(
    () => new UserColumnsFactory(intl, timezone).create(),
    [intl, timezone]
  );

  const slotProps = useUserDataGridSlotProps({ columns });

  const config = useMemo(
    () =>
      createClientDataGridConfig<UserOnView>(
        DataGridInstanceName.user,
        INITIAL_SORT_MODEL
      )
        .withPagination({ defaultPageSize: DEFAULT_PAGE_SIZE, pageSizeOptions: PAGE_SIZE_OPTIONS })
        .withRowDoubleClick((params) => {
          if (params.row && params.row._id) {
            goToUserDetails(params.row._id);
          }
        })
        .withNoRowsOverlay(SearchPhraseMismatchNoRowsOverlay)
        .withSlotProps(slotProps)
        .withAutosizing()
        .build(),
    [goToUserDetails, slotProps]
  );

  return (
    <DataGrid
      data={data}
      columns={columns}
      config={config}
      loading={loading}
      emptyListReason={emptyListReason}
      styles={sxStyles}
    />
  );
}
